import { useEffect, useState } from 'react';

import { Constants } from '@constants';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight } from '@iwoca/icons';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import { Button, Text } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { getOfferWithHighestSuitability } from './getOfferWithHighestSuitability';
import {
    getLoanDurationMapping,
    getLoanDurationMappingDE,
    getLoanPurposeMapping,
    getLoanPurposeMappingDE
} from './RecommendedOffer.helpers';
import { RecommendedOfferContentUI } from './RecommendedOfferContent';
import { RecommendedOfferDetails } from './RecommendedOfferDetails';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { FooterSectionWrapper } from '../../../pageStructure/footerSectionWrapper/FooterSectionwraper';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import { TrustPilot } from '../../../TrustPilot/TrustPilot';

type TSuitabilityInfo = {
    score_inputs: {
        qualitative_duration?: string;
        requested_duration?: string;
        application_purpose: string;
    };
    suitability_score: number;
};

export const RecommendedOfferView = ({
    setShowAllOptions
}: {
    setShowAllOptions: (showAllOptions: boolean) => void;
}) => {
    const stateKey = useStateKey();

    const [drawdownAmount, setDrawdownAmount] = useState<number>(0);

    const {
        isLoading,
        isError,
        data: offerWithHighestSuitability
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey }),
        select: (data) => getOfferWithHighestSuitability(data)
    });

    useEffect(() => {
        if (offerWithHighestSuitability) {
            setDrawdownAmount(offerWithHighestSuitability.available_cash || 0);
        }
    }, [offerWithHighestSuitability, setDrawdownAmount]);

    if (isError) {
        return <FallbackErrorModal />;
    }

    if (isLoading || drawdownAmount === 0) {
        return <MainContentPending />;
    }

    if (!isError && !isLoading && offerWithHighestSuitability === null) {
        return <FallbackErrorModal />;
    }

    const loanDuration = getDurationInMonths(
        offerWithHighestSuitability?.duration!
    );

    let qualitativeDuration = null;
    let requestedDuration = null;
    let applicationPurpose = null;

    //lapi schema to be updated, casting this type is needed to avoid TS errors
    const suitability_info =
        offerWithHighestSuitability?.suitability_info as unknown as TSuitabilityInfo;

    if (suitability_info) {
        qualitativeDuration =
            suitability_info.score_inputs.qualitative_duration;
        requestedDuration = suitability_info.score_inputs.requested_duration;
        applicationPurpose = suitability_info.score_inputs.application_purpose;
    }

    const interestOnlyPeriod = offerWithHighestSuitability
        ? getFormalOfferInterestOnlyPeriod(offerWithHighestSuitability)
        : 0;

    return (
        <MainSectionWrapper className="w-ful min-h-screen">
            <div className="mb-4xl flex flex-col items-center justify-center gap-s">
                <h1 className="text-center max-sm:mb-xl max-sm:leading-10">
                    <FormattedMessage
                        id="_.recommended_offer_view.heading"
                        defaultMessage="You're approved for up to {amount}"
                        values={{
                            amount: (
                                <span className="text-success-drk">
                                    <FormattedCurrency
                                        value={
                                            offerWithHighestSuitability!
                                                .available_cash!
                                        }
                                    />
                                </span>
                            )
                        }}
                    />
                </h1>
                <div>
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.timeout"
                        defaultMessage="This offer is valid until {timeout}"
                        values={{
                            timeout: (
                                <FormattedDate
                                    value={
                                        new Date(
                                            offerWithHighestSuitability?.timeout!
                                        )
                                    }
                                    day="2-digit"
                                    month="long"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                </div>
            </div>

            <div className="flex items-center justify-center rounded-t-m border border-b-0 border-structure-95 bg-success-drk text-center text-s text-structure-100 max-sm:mx-m max-sm:flex-col">
                <p className="font-bold">
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.relevant_offer_label"
                        defaultMessage="Our most relevant offer for you:"
                    />
                </p>
                <p className="font-reg max-sm:mb-s max-sm:mt-[-10px] max-sm:p-0">
                    &nbsp;
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.relevant_offer_text"
                        defaultMessage="You want to borrow funds for {requestedDuration} for {requestedPurpose}."
                        values={{
                            requestedDuration: (
                                <span className="font-med">
                                    {getCountry() === 'DE'
                                        ? getLoanDurationMappingDE(
                                              (qualitativeDuration ||
                                                  requestedDuration)!
                                          )
                                        : getLoanDurationMapping(
                                              qualitativeDuration!
                                          )}
                                </span>
                            ),
                            //DE hide requested purpose if requested purpose is 'other'
                            requestedPurpose: applicationPurpose !==
                                'other' && (
                                <span className="font-med">
                                    {getCountry() === 'DE'
                                        ? getLoanPurposeMappingDE(
                                              applicationPurpose!
                                          )
                                        : getLoanPurposeMapping(
                                              applicationPurpose!
                                          )}
                                </span>
                            )
                        }}
                    />
                </p>
            </div>
            <div className="flex w-full flex-row items-start justify-evenly rounded-b-m border border-structure-95 bg-structure-100 shadow-blue-s max-sm:flex-col max-sm:bg-transparent">
                <RecommendedOfferContentUI
                    loanDuration={loanDuration!}
                    minimumDrawdownAmount={Constants.MIN_DRAW_DOWN_AMOUNT}
                    setDrawdownAmount={setDrawdownAmount}
                    drawdownAmount={drawdownAmount}
                    maximumDrawdownAmount={
                        offerWithHighestSuitability?.available_cash!
                    }
                    offerTimeout={offerWithHighestSuitability?.timeout!}
                    offerId={offerWithHighestSuitability?.offer_id!}
                    productType={offerWithHighestSuitability?.product_type!}
                    customTitle={
                        offerWithHighestSuitability &&
                        interestOnlyPeriod === 6 ? (
                            <div className="mb-xl mt-l">
                                <Text className="text-2xl font-bold">
                                    <FormattedMessage
                                        id="_.recommended_offer_view.interest_only_plus_title"
                                        defaultMessage="{interestOnlyPeriod} months flexible credit limit, <span>then {remainingLoanDuration} monthly repayments</span>"
                                        values={{
                                            interestOnlyPeriod:
                                                interestOnlyPeriod,
                                            remainingLoanDuration:
                                                getDurationInMonths(
                                                    offerWithHighestSuitability.duration!
                                                ) - interestOnlyPeriod,
                                            span: (chunks) => (
                                                <span className="text-l font-reg">
                                                    {chunks}
                                                </span>
                                            )
                                        }}
                                    />
                                </Text>
                            </div>
                        ) : null
                    }
                    interestOnlyPeriod={interestOnlyPeriod}
                />
                <RecommendedOfferDetails
                    drawdownAmount={drawdownAmount}
                    recommendedOffer={offerWithHighestSuitability!}
                    loanDuration={loanDuration!}
                />
            </div>
            <div className="mt-l flex flex-row items-center justify-center rounded-l bg-secondary-90 p-l text-center text-s font-med max-sm:flex-col">
                <p className="mr-l">
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.see_all_options"
                        defaultMessage="This is not financial advice and you can explore your full range of options."
                    />
                </p>
                <Button
                    variant="secondary-alt"
                    onClick={() => {
                        setShowAllOptions(true);
                        tracking.ampli.exploreMoreOptionsClicked({
                            recommended_offer_id:
                                offerWithHighestSuitability?.offer_id!,
                            recommended_offer_duration: loanDuration!.toString()
                        });
                    }}
                >
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.see_all_options_btn"
                        defaultMessage="Show me all of my options"
                    />
                    <IconAngleRight className="fill-primary-40" />
                </Button>
            </div>
            <FooterSectionWrapper>
                {getCountry() === 'UK' && <TrustPilot />}
            </FooterSectionWrapper>
        </MainSectionWrapper>
    );
};
